import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/App';
import { Link } from 'react-router-dom';
import { services as svcApi, wallet as walletApi, subscription as subApi } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Database, Cpu, HardDrive, Radio, Check, Loader2, Wallet, Clock, Calculator, ShieldCheck, ArrowRight, AlertTriangle, Calendar, Edit3, RefreshCw, Plus } from 'lucide-react';
import { Switch } from '@/components/ui/switch';

const categoryIcons = { database: Database, ml_model: Cpu, storage: HardDrive, streaming: Radio, ai_search: Database };
const tierConfig = {
  economy: { label: 'Economy', color: 'text-slate-400 border-slate-600', bg: 'bg-slate-500/10' },
  standard: { label: 'Standard', color: 'text-cyan-400 border-cyan-600', bg: 'bg-cyan-500/10' },
  priority: { label: 'Priority', color: 'text-amber-400 border-amber-600', bg: 'bg-amber-500/10' },
};
const categoryLabels = { database: 'Databases', ml_model: 'ML Models', storage: 'Storage', streaming: 'Streaming', ai_search: 'AI & Search' };

export default function Services() {
  const { user, refreshUser } = useAuth();
  const [allServices, setAllServices] = useState({});
  const [walletBalance, setWalletBalance] = useState(null);
  const [activeSub, setActiveSub] = useState(null);
  const [hasActive, setHasActive] = useState(false);

  // Phase 8 — Account-Level Tier: one tier for all services in the subscription
  const [accountTier, setAccountTier] = useState('economy');
  // Selection state (for new/modify flow): { [svcId]: accountTier } — always in sync with accountTier
  const [selected, setSelected] = useState({});
  const [duration, setDuration] = useState(30);
  const [hoursPerDay, setHoursPerDay] = useState(24);
  const [proceeding, setProceeding] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [modifyMode, setModifyMode] = useState(false);

  // Add services mid-subscription state — tier locked to active sub's tier
  const [showAddServices, setShowAddServices] = useState(false);
  const [addSelected, setAddSelected] = useState({});  // { [svcId]: lockedTier }
  const [addProceeding, setAddProceeding] = useState(false);

  const loadData = () => {
    svcApi.list().then(r => setAllServices(r.data.services || {})).catch(() => {});
    walletApi.get().then(r => setWalletBalance(r.data.balance)).catch(() => {});
    subApi.current().then(r => {
      setHasActive(r.data.has_active);
      setActiveSub(r.data.subscription);
    }).catch(() => {});
  };

  useEffect(() => { loadData(); }, []);

  // Phase 8 — when account tier changes, re-key every selected service to the new tier
  useEffect(() => {
    setSelected(prev => {
      const next = {};
      Object.keys(prev).forEach(svcId => { next[svcId] = accountTier; });
      return next;
    });
  }, [accountTier]);

  const toggleService = (id) => setSelected(prev => {
    const next = { ...prev };
    if (next[id]) delete next[id]; else next[id] = accountTier;
    return next;
  });

  const { totalCost, dailyCost, diff } = useMemo(() => {
    const rate = Object.keys(selected).reduce((sum, svcId) => sum + (allServices[svcId]?.pricing?.[accountTier]?.hourly_rate || 0), 0);
    const daily = rate * hoursPerDay;
    const total = daily * duration;
    return { totalCost: Math.round(total * 100) / 100, dailyCost: Math.round(daily * 100) / 100, diff: Math.round(((walletBalance || 0) - total) * 100) / 100 };
  }, [selected, allServices, walletBalance, duration, hoursPerDay, accountTier]);

  const handleProceed = async () => {
    setProceeding(true);
    try {
      const services = Object.keys(selected);
      const res = await subApi.proceed({ tier: accountTier, services, duration_days: duration, hours_per_day: hoursPerDay });
      toast.success(`Subscription activated! ${res.data.services.length} services at ${accountTier} for ${duration} days.`);
      setShowConfirm(false);
      setSelected({});
      await refreshUser();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Subscription failed');
    }
    setProceeding(false);
  };

  const handleQueueChanges = async () => {
    setProceeding(true);
    try {
      const services = Object.keys(selected);
      const res = await subApi.queueChanges({ tier: accountTier, services });
      toast.success(res.data.message);
      setModifyMode(false);
      setSelected({});
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to queue changes'); }
    setProceeding(false);
  };

  // Add services mid-subscription — tier is LOCKED to the active sub's tier
  const lockedAddTier = activeSub?.tier || 'economy';
  const toggleAddService = (id) => setAddSelected(prev => {
    const next = { ...prev };
    if (next[id]) delete next[id]; else next[id] = lockedAddTier;
    return next;
  });

  const addableServices = useMemo(() => {
    if (!activeSub) return {};
    const currentIds = new Set((activeSub.services || []).map(s => s.service));
    const result = {};
    Object.entries(allServices).forEach(([id, svc]) => {
      if (!currentIds.has(id) && svc.is_available) result[id] = svc;
    });
    return result;
  }, [allServices, activeSub]);

  const addCostInfo = useMemo(() => {
    if (!activeSub) return { total: 0, items: [] };
    const remaining = activeSub.days_remaining || 1;
    const hpd = activeSub.hours_per_day || 24;
    const items = Object.keys(addSelected).map(svcId => {
      const rate = allServices[svcId]?.pricing?.[lockedAddTier]?.hourly_rate || 0;
      const cost = rate * hpd * remaining;
      return { svcId, tier: lockedAddTier, rate, cost: Math.round(cost * 100) / 100, name: allServices[svcId]?.name || svcId };
    });
    const total = Math.round(items.reduce((s, i) => s + i.cost, 0) * 100) / 100;
    return { total, items, remaining, hpd };
  }, [addSelected, allServices, activeSub, lockedAddTier]);

  const handleAddServices = async () => {
    setAddProceeding(true);
    try {
      const services = Object.keys(addSelected);  // names only — tier locked to sub on server
      const res = await subApi.addServices({ services });
      toast.success(`${res.data.added_services.length} service(s) added at ${res.data.tier || lockedAddTier}! Pro-rated cost: $${res.data.pro_rated_cost.toFixed(2)}`);
      setShowAddServices(false);
      setAddSelected({});
      await refreshUser();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add services');
    }
    setAddProceeding(false);
  };

  const categories = {};
  Object.entries(allServices).forEach(([id, svc]) => {
    if (!categories[svc.category]) categories[svc.category] = [];
    categories[svc.category].push({ ...svc, id });
  });

  const selectionCount = Object.keys(selected).length;

  // ── PHASE 2: Active Subscription View ──
  if (hasActive && activeSub && !modifyMode) {
    const pending = activeSub.pending_changes;
    return (
      <div data-testid="services-page" className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Your Subscription</h2>
          <p className="text-muted-foreground mt-1">Active service subscription</p>
        </div>

        <Card className="bg-card border-emerald-500/30 shadow-[0_0_15px_rgba(16,185,129,0.08)]">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-400" />
                <span className="font-bold text-emerald-400">ACTIVE</span>
                {activeSub.tier && (
                  <Badge variant="outline" className={`text-xs font-bold ${tierConfig[activeSub.tier]?.color || ''}`} data-testid="active-sub-tier">
                    {tierConfig[activeSub.tier]?.label || activeSub.tier}
                  </Badge>
                )}
                <span className="text-sm text-muted-foreground">— {activeSub.days_remaining} days remaining</span>
              </div>
              <Badge variant="outline" className="font-mono text-xs">${activeSub.total_cost?.toFixed(2)} paid</Badge>
            </div>
            <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Started: {activeSub.start_date?.slice(0, 10)}</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Expires: {activeSub.end_date?.slice(0, 10)}</span>
              <span>{activeSub.duration_days} days, {activeSub.hours_per_day}h/day</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {(activeSub.services || []).map(s => {
                const info = allServices[s.service];
                const tc = tierConfig[s.latency_tier];
                return (
                  <div key={s.service} data-testid={`active-svc-${s.service}`} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border/30">
                    <div>
                      <p className="text-sm font-medium">{info?.name || s.service}</p>
                    </div>
                    <Badge variant="outline" className={`text-xs ${tc?.color || ''}`}>{tc?.label || s.latency_tier}</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Auto-Renewal Status */}
        <Card className="bg-card border-border/50">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-primary" />
                <span className="text-sm font-bold">Auto-Renewal</span>
              </div>
              <Switch checked={activeSub.auto_renew !== false} onCheckedChange={async (v) => {
                try {
                  await subApi.toggleAutoRenew({ enabled: v });
                  toast.success(v ? 'Auto-renew enabled' : 'Auto-renew disabled');
                  loadData();
                } catch (err) { toast.error('Failed to update'); }
              }} />
            </div>
            {activeSub.auto_renew !== false ? (
              <div className="space-y-2">
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">Next renewal</span><span>{activeSub.end_date?.slice(0, 10)}</span></div>
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">Renewal cost</span><span className="font-mono font-bold">${(activeSub.renewal_cost || activeSub.total_cost)?.toFixed(2)}</span></div>
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">Wallet balance</span><span className="font-mono font-bold">${walletBalance?.toFixed(2) || '—'}</span></div>
                {walletBalance !== null && (walletBalance < (activeSub.renewal_cost || activeSub.total_cost)) ? (
                  <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <p className="text-[10px] text-amber-400 flex items-center gap-1 mb-1.5"><AlertTriangle className="w-3 h-3" />Insufficient balance for renewal</p>
                    <p className="text-[10px] text-muted-foreground">Add at least <span className="font-mono font-bold">${((activeSub.renewal_cost || activeSub.total_cost) - walletBalance).toFixed(2)}</span> before {activeSub.end_date?.slice(0, 10)} to avoid service interruption.</p>
                    <Button asChild size="sm" className="h-6 text-[10px] mt-1.5 bg-primary hover:bg-primary/90"><Link to="/dashboard/billing"><Wallet className="w-3 h-3 mr-1" />Add Credit</Link></Button>
                  </div>
                ) : walletBalance !== null ? (
                  <p className="text-[10px] text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" />Balance sufficient for renewal</p>
                ) : null}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Your subscription will expire on {activeSub.end_date?.slice(0, 10)}. You'll need to resubscribe manually.</p>
            )}
          </CardContent>
        </Card>

        {/* Pending Changes */}
        {pending && (
          <Card className="bg-card border-amber-500/30">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-bold text-amber-400">Pending Changes</span>
                  <span className="text-xs text-muted-foreground">Effective: {activeSub.end_date?.slice(0, 10)}</span>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs border-destructive/30 text-destructive" onClick={async () => {
                  try { await subApi.cancelPending(); toast.success('Pending changes cancelled'); loadData(); } catch (err) { toast.error('Failed'); }
                }}>Cancel Changes</Button>
              </div>
              <div className="space-y-1">
                {pending.services?.map(s => (
                  <div key={s.service} className="flex justify-between text-sm p-2 rounded bg-secondary/20"><span>{allServices[s.service]?.name || s.service}</span><Badge variant="outline" className="text-[10px]">{s.latency_tier}</Badge></div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex gap-3 flex-wrap">
          <Button data-testid="add-services-now-btn" onClick={() => { setShowAddServices(true); setAddSelected({}); }} className="flex items-center gap-2 bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4" />Add Services Now
          </Button>
          <Button onClick={() => { setModifyMode(true); setSelected({}); }} variant="outline" className="flex items-center gap-2">
            <Edit3 className="w-4 h-4" />Modify Services for Next Period
          </Button>
          <Button variant="outline" className="flex items-center gap-2" onClick={async () => {
            if (!window.confirm(`Renew now for $${(activeSub.renewal_cost || activeSub.total_cost)?.toFixed(2)}? Your current remaining days will be replaced with a fresh ${activeSub.duration_days}-day period.`)) return;
            try {
              const res = await subApi.renewNow();
              toast.success(`Renewed! ${res.data.services.length} services for ${activeSub.duration_days} days. $${res.data.total_cost} deducted.`);
              await refreshUser();
              loadData();
            } catch (err) { toast.error(err.response?.data?.detail || 'Renewal failed'); }
          }}>
            <RefreshCw className="w-4 h-4" />Renew Now
          </Button>
        </div>

        {/* Add Services Mid-Subscription Dialog */}
        <Dialog open={showAddServices} onOpenChange={setShowAddServices}>
          <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-primary" />Add Services to Current Period</DialogTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Pro-rated for {addCostInfo.remaining} remaining day{addCostInfo.remaining !== 1 ? 's' : ''} at {addCostInfo.hpd}h/day.{' '}
                <span data-testid="add-services-locked-tier">
                  New services will run at your subscription tier:{' '}
                  <span className={`font-bold ${tierConfig[lockedAddTier]?.color || ''}`}>{tierConfig[lockedAddTier]?.label || lockedAddTier}</span>
                </span>
              </p>
            </DialogHeader>
            <div className="space-y-4">
              {Object.keys(addableServices).length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">All available services are already in your subscription.</p>
              ) : (
                <>
                  <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                    {Object.entries(addableServices).map(([id, svc]) => {
                      const isSelected = !!addSelected[id];
                      const rateAtTier = svc.pricing?.[lockedAddTier]?.hourly_rate || 0;
                      return (
                        <div key={id} data-testid={`add-svc-${id}`} className={`p-3 rounded-lg border transition-all ${isSelected ? 'border-primary/40 bg-primary/5' : 'border-border/40 bg-secondary/10'}`}>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              data-testid={`add-svc-checkbox-${id}`}
                              checked={isSelected}
                              onChange={() => toggleAddService(id)}
                              className="w-3.5 h-3.5 rounded accent-primary"
                            />
                            <span className="text-sm font-medium flex-1">{svc.name}</span>
                            <span className="text-xs font-mono text-muted-foreground" data-testid={`add-svc-rate-${id}`}>${rateAtTier}/hr</span>
                            {isSelected && <Check className="w-3 h-3 text-emerald-400" />}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Cost breakdown */}
                  {addCostInfo.items.length > 0 && (
                    <div className="p-3 rounded-lg bg-secondary/20 border border-border/20 space-y-1.5">
                      {addCostInfo.items.map(item => (
                        <div key={item.svcId} className="flex justify-between text-xs">
                          <span>{item.name} <span className="text-muted-foreground">({item.tier})</span></span>
                          <span className="font-mono font-bold">${item.cost.toFixed(2)}</span>
                        </div>
                      ))}
                      <div className="border-t border-border/30 pt-1.5 flex justify-between text-sm font-bold">
                        <span>Pro-rated Total</span>
                        <span className="font-mono">${addCostInfo.total.toFixed(2)}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">For {addCostInfo.remaining} remaining days. At renewal, full-period pricing applies.</p>
                      {walletBalance !== null && (
                        <div className="flex justify-between text-xs text-muted-foreground pt-1">
                          <span>Wallet balance</span>
                          <span className="font-mono">${walletBalance.toFixed(2)}</span>
                        </div>
                      )}
                      {walletBalance !== null && walletBalance < addCostInfo.total && (
                        <div className="p-2 rounded bg-destructive/10 border border-destructive/20 text-[10px] text-destructive mt-1">
                          Insufficient balance. Need ${(addCostInfo.total - walletBalance).toFixed(2)} more.
                          <Button asChild size="sm" className="w-full h-6 text-[10px] mt-1 bg-primary hover:bg-primary/90"><Link to="/dashboard/billing"><Wallet className="w-3 h-3 mr-1" />Add Credit</Link></Button>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowAddServices(false)}>Cancel</Button>
                    <Button data-testid="confirm-add-services-btn" onClick={handleAddServices}
                      disabled={Object.keys(addSelected).length === 0 || addProceeding || (walletBalance !== null && walletBalance < addCostInfo.total)}
                      className="bg-primary hover:bg-primary/90">
                      {addProceeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Add & Pay ${addCostInfo.total.toFixed(2)}</>}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Subscription History */}
        <SubscriptionHistory allServices={allServices} />
      </div>
    );
  }

  // ── PHASE 1: Selection Flow (new subscription or modify for next period) ──
  return (
    <div data-testid="services-page" className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">{modifyMode ? 'Modify Services (Next Period)' : 'Service Marketplace'}</h2>
        <p className="text-muted-foreground mt-1">
          {modifyMode ? 'Select services for your next subscription period. These changes will take effect when your current period expires.' : 'Select services, choose tiers, set duration, and proceed to activate.'}
        </p>
        {modifyMode && <Button size="sm" variant="ghost" className="text-xs mt-1" onClick={() => setModifyMode(false)}>Back to Active Subscription</Button>}
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left: Service cards */}
        <div className="flex-1 space-y-5">

          {/* Phase 8 — Account-Level Tier selector (Step 1). All 3 tiers selectable by any user. */}
          <Card className="bg-card border-border/50" data-testid="account-tier-selector">
            <CardContent className="p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                Step 1 — Pick your account tier
              </p>
              <div className="grid grid-cols-3 gap-2">
                {['economy', 'standard', 'priority'].map(t => {
                  const tc = tierConfig[t];
                  const isActive = accountTier === t;
                  return (
                    <button
                      key={t}
                      data-testid={`tier-option-${t}`}
                      onClick={() => setAccountTier(t)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${isActive ? `${tc.bg} ${tc.color} border-current` : 'border-border/40 text-muted-foreground hover:text-foreground'}`}
                    >
                      <p className="text-sm font-bold">{tc.label}</p>
                      <p className="text-[10px] mt-0.5 opacity-80">
                        {t === 'economy' ? 'Best value' : t === 'standard' ? 'Balanced' : 'Fastest, dedicated routing'}
                      </p>
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">
                One tier applies to every service you select. You can change tier next period via "Modify".
              </p>
            </CardContent>
          </Card>

          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Step 2 — Select services (rates shown at {tierConfig[accountTier]?.label})
          </p>

          {Object.entries(categories).map(([cat, svcs]) => {
            const Icon = categoryIcons[cat] || Cpu;
            return (
              <div key={cat} className="space-y-2">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-primary" />
                  <h3 className="text-sm font-semibold">{categoryLabels[cat] || cat}</h3>
                </div>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
                  {svcs.map(svc => {
                    const isSelected = !!selected[svc.id];
                    const rateAtTier = svc.pricing?.[accountTier]?.hourly_rate || 0;
                    return (
                      <Card key={svc.id} data-testid={`service-card-${svc.id}`} className={`bg-card border-border/50 transition-all ${isSelected ? 'border-primary/40 shadow-sm' : ''}`}>
                        <CardContent className="p-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              data-testid={`service-checkbox-${svc.id}`}
                              checked={isSelected}
                              onChange={() => toggleService(svc.id)}
                              disabled={!svc.is_available}
                              className="w-3.5 h-3.5 rounded border-slate-300 accent-primary disabled:opacity-40"
                            />
                            <span className="text-sm font-medium flex-1">{svc.name}</span>
                            <span className="text-xs font-mono font-bold text-foreground" data-testid={`service-rate-${svc.id}`}>${rateAtTier}/hr</span>
                            {isSelected && <Check className="w-3 h-3 text-emerald-400" />}
                          </div>
                          {!svc.is_available && (
                            <p className="text-[10px] text-muted-foreground">Unavailable</p>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Right: Cost calculator + Proceed */}
        <div className="lg:w-80 shrink-0">
          <div className="sticky top-20">
            <Card className="bg-card border-border/50">
              <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Calculator className="w-4 h-4 text-primary" />{modifyMode ? 'Next Period' : 'Your Selection'}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {selectionCount === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">Select services to continue</p>
                ) : (
                  <>
                    {!modifyMode && (
                      <>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Duration</Label>
                          <div className="flex gap-1.5">
                            {[7, 14, 30].map(d => (
                              <button key={d} onClick={() => setDuration(d)} className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-all ${duration === d ? 'bg-primary/15 text-primary border-primary/40' : 'border-border/40 text-muted-foreground'}`}>{d}d</button>
                            ))}
                            <Input type="number" min="1" max="365" value={duration} onChange={e => setDuration(Math.max(1, Math.min(365, parseInt(e.target.value) || 1)))} className="w-14 h-8 text-xs font-mono text-center" />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Hours per day</Label>
                          <div className="flex items-center gap-2">
                            <input type="range" min="1" max="24" value={hoursPerDay} onChange={e => setHoursPerDay(parseInt(e.target.value))} className="flex-1 h-1.5 bg-secondary rounded-lg accent-primary" />
                            <span className="text-xs font-bold font-mono text-primary w-8 text-center">{hoursPerDay}h</span>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Per-service breakdown */}
                    <div className="space-y-1">
                      {Object.keys(selected).map((svcId) => {
                        const info = allServices[svcId];
                        const rate = info?.pricing?.[accountTier]?.hourly_rate || 0;
                        const cost = rate * hoursPerDay * duration;
                        return (
                          <div key={svcId} className="flex justify-between text-xs py-1 border-b border-border/20 last:border-0">
                            <span className="truncate mr-2">{info?.name}</span>
                            <span className="font-mono font-bold shrink-0">${cost.toFixed(2)}</span>
                          </div>
                        );
                      })}
                    </div>

                    {!modifyMode && (
                      <>
                        <div className="border-t border-border/50 pt-2 space-y-1">
                          <div className="flex justify-between text-sm font-bold"><span>Total ({duration}d)</span><span className="font-mono">${totalCost.toFixed(2)}</span></div>
                          {walletBalance !== null && (
                            <>
                              <div className="flex justify-between text-xs text-muted-foreground"><span>Wallet</span><span className="font-mono">${walletBalance.toFixed(2)}</span></div>
                              <div className={`flex justify-between text-sm font-bold ${diff >= 0 ? 'text-emerald-400' : 'text-destructive'}`}>
                                <span>{diff >= 0 ? 'Remaining' : 'Shortfall'}</span>
                                <span className="font-mono">{diff >= 0 ? `$${diff.toFixed(2)}` : `-$${Math.abs(diff).toFixed(2)}`}</span>
                              </div>
                            </>
                          )}
                        </div>

                        {diff < 0 && (
                          <div className="p-2 rounded-lg bg-destructive/10 border border-destructive/20 text-[10px] text-destructive">
                            Add ${Math.abs(diff).toFixed(2)} more credit.
                            <Button asChild size="sm" className="w-full h-6 text-[10px] mt-1 bg-primary hover:bg-primary/90"><Link to="/dashboard/billing"><Wallet className="w-3 h-3 mr-1" />Add Credit</Link></Button>
                          </div>
                        )}

                        <Button data-testid="proceed-btn" onClick={() => setShowConfirm(true)} disabled={selectionCount === 0 || diff < 0}
                          className="w-full bg-primary hover:bg-primary/90 shadow-md">
                          <ShieldCheck className="w-4 h-4 mr-2" />Proceed — Pay ${totalCost.toFixed(2)}
                        </Button>
                      </>
                    )}

                    {modifyMode && (
                      <Button data-testid="queue-changes-btn" onClick={handleQueueChanges} disabled={selectionCount === 0 || proceeding}
                        className="w-full bg-amber-500 hover:bg-amber-600 text-white">
                        {proceeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Queue Changes for Next Period</>}
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" />Confirm Subscription</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-secondary/20 border border-border/20 space-y-1">
              {Object.entries(selected).map(([svcId, tier]) => (
                <div key={svcId} className="flex justify-between text-sm"><span>{allServices[svcId]?.name} ({tier})</span><span className="font-mono">${((allServices[svcId]?.pricing?.[tier]?.hourly_rate || 0) * hoursPerDay * duration).toFixed(2)}</span></div>
              ))}
              <div className="border-t border-border/30 pt-1 mt-1 flex justify-between font-bold"><span>Total</span><span className="font-mono">${totalCost.toFixed(2)}</span></div>
            </div>
            <p className="text-xs text-muted-foreground">{duration} days at {hoursPerDay} hours/day. This is a prepaid commitment. Amount will be deducted from your wallet immediately.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowConfirm(false)}>Cancel</Button>
              <Button onClick={handleProceed} disabled={proceeding} className="bg-primary hover:bg-primary/90">
                {proceeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Confirm & Pay ${totalCost.toFixed(2)}</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function SubscriptionHistory({ allServices }) {
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    subApi.history({ limit: 20 }).then(r => { setHistory(r.data.history || []); setTotal(r.data.total || 0); }).catch(() => {});
  }, []);

  // Filter to show only past (non-active) subscriptions
  const past = history.filter(s => s.status !== 'active');
  if (past.length === 0) return null;

  return (
    <Card className="bg-card border-border/50">
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-muted-foreground" />Subscription History</span>
          <Badge variant="outline" className="text-xs">{past.length}</Badge>
        </CardTitle>
      </CardHeader>
      {expanded && (
        <CardContent>
          <div className="space-y-2">
            {past.map((sub, i) => (
              <div key={i} className="p-3 rounded-lg bg-secondary/20 border border-border/20">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <Badge className={sub.status === 'expired' ? 'bg-slate-500/20 text-slate-400' : sub.status === 'cancelled' ? 'bg-destructive/20 text-destructive' : 'bg-secondary'} variant="default">
                      {sub.early_renewal ? 'Renewed Early' : sub.status}
                    </Badge>
                    <span className="text-xs text-muted-foreground">{sub.duration_days} days</span>
                  </div>
                  <span className="text-xs font-mono font-bold">${sub.total_cost?.toFixed(2)}{sub.total_cost === 0 ? ' (granted)' : ''}</span>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-muted-foreground mb-1.5">
                  <span>{sub.start_date?.slice(0, 10)} → {sub.end_date?.slice(0, 10)}</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(sub.services || []).map(s => (
                    <span key={s.service} className="px-1.5 py-0.5 rounded bg-secondary/50 text-[9px]">{allServices[s.service]?.name || s.service} ({s.latency_tier})</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
